import { useCallback, useEffect, useRef } from 'react';
import { Editor } from '@tiptap/core';
import { SearchState, SearchReplaceHookResult } from '../types';
import { hasExtension } from 'components/EditorBar/utils';

export const useSearchReplace = (
  editor: Editor | null,
  searchState: SearchState,
  resetSearchTerms: () => void,
  open: boolean,
): SearchReplaceHookResult => {
  const { searchTerm, replaceTerm, caseSensitive, wholeWord } = searchState;
  const hasSearchReplaceExt = hasExtension(editor, 'searchAndReplace');
  const prevOpenRef = useRef(open);
  const prevEditorRef = useRef<Editor | null>(null);
  // Ref to latest editor for unmount cleanup
  const editorRef = useRef<Editor | null>(null);
  editorRef.current = editor;

  // Helper for common editor checks
  const withEditorCheck = useCallback(
    (callback: (editor: Editor) => void) => {
      if (!editor || !hasSearchReplaceExt) return;
      callback(editor);
    },
    [editor, hasSearchReplaceExt],
  );

  const goToSelection = useCallback(() => {
    withEditorCheck((editor) => {
      const { results, resultIndex } = editor.storage.searchAndReplace;
      const position = results[resultIndex];
      if (!position) return;

      const { node } = editor.view.domAtPos(position.from);
      if (node instanceof HTMLElement) {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }, [withEditorCheck]);

  const updateSearchReplace = useCallback(
    (clearIndex = false) => {
      withEditorCheck((editor) => {
        if (clearIndex) editor.commands.resetIndex();
        editor.commands.setSearchTerm(searchTerm);
        editor.commands.setReplaceTerm(replaceTerm);
        editor.commands.setCaseSensitive(caseSensitive);
        editor.commands.setWholeWord(wholeWord);
        goToSelection();
      });
    },
    [withEditorCheck, searchTerm, replaceTerm, caseSensitive, wholeWord, goToSelection],
  );

  const clear = useCallback(() => {
    withEditorCheck((editor) => {
      // Use the new clearSearch command to fully clear highlighting
      editor.commands.clearSearch();
      resetSearchTerms();
    });
  }, [withEditorCheck, resetSearchTerms]);

  // Create handlers with a factory function to reduce repetition
  const createActionHandler = useCallback(
    (action: (editor: Editor) => void, needsSelection = true) => {
      return () => {
        withEditorCheck((editor) => {
          action(editor);
          if (needsSelection) goToSelection();
        });
      };
    },
    [withEditorCheck, goToSelection],
  );

  const actionHandlers = {
    replace: useCallback(
      () => createActionHandler((editor) => editor.commands.replace())(),
      [createActionHandler],
    ),
    next: useCallback(
      () => createActionHandler((editor) => editor.commands.nextSearchResult())(),
      [createActionHandler],
    ),
    previous: useCallback(
      () => createActionHandler((editor) => editor.commands.previousSearchResult())(),
      [createActionHandler],
    ),
    replaceAll: useCallback(
      () => createActionHandler((editor) => editor.commands.replaceAll(), false)(),
      [createActionHandler],
    ),
  };

  // Effects
  useEffect(() => {
    if (editor && open) {
      const timeoutId = setTimeout(updateSearchReplace, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [editor, updateSearchReplace, open]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      clear();
    } else {
      updateSearchReplace(true);
    }
  }, [searchTerm, clear, updateSearchReplace]);

  // Clear search highlighting when menu closes
  useEffect(() => {
    // Only run when transitioning from open to closed
    if (prevOpenRef.current && !open && editor) {
      clear();
    }
    prevOpenRef.current = open;
  }, [open, clear, editor]);

  // Handle editor switching: clear highlights on old editor, preserve search terms
  // when the new editor also has the extension
  useEffect(() => {
    const prevEditor = prevEditorRef.current;
    prevEditorRef.current = editor;

    if (prevEditor && prevEditor !== editor) {
      // Clear highlights on the previous editor
      if (hasExtension(prevEditor, 'searchAndReplace')) {
        prevEditor.commands.clearSearch();
      }

      // If the new editor lacks the extension, reset search terms so state is clean
      if (!hasExtension(editor, 'searchAndReplace')) {
        resetSearchTerms();
      }
      // When the new editor HAS the extension, search terms are preserved and
      // the updateSearchReplace effect above re-applies them to the new editor
    }
  }, [editor, resetSearchTerms]);

  // Cleanup on unmount: clear highlights on the current editor
  useEffect(() => {
    return () => {
      const currentEditor = editorRef.current;
      if (currentEditor && hasExtension(currentEditor, 'searchAndReplace')) {
        currentEditor.commands.clearSearch();
      }
    };
  }, []);

  // Results counter
  const resultIndex = editor?.storage?.searchAndReplace?.resultIndex ?? 0;
  const resultsLength = editor?.storage?.searchAndReplace?.results?.length ?? 0;

  return {
    goToSelection,
    updateSearchReplace,
    clear,
    actionHandlers,
    resultIndex,
    resultsLength,
  };
};
